import { useMutation, useQueryClient } from '@tanstack/react-query'

import { BankService } from '@/api/services/BankService'
import { connectionQueryKeys } from '@/api/services/connectionQueryKeys'

// Takes the customer the dialog was opened for, so the delete cannot land on a customer another
// tab has since selected.
export const useDeleteConnection = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, string>({
    mutationFn: connectionId => BankService.deleteConnection(userId, connectionId),
    // The deleted connection's own reads are dropped rather than refetched (they would 404), then
    // the connection list and every account list are read again so the page shows what is left.
    // The transaction filters are built from the same connections and accounts, so they go too.
    //
    // The reload is returned so the mutation stays pending until the list is back: the loader then
    // covers the whole delete, and the dialog closes as the deleted connection leaves the page.
    onSuccess: (_, connectionId) => {
      queryClient.removeQueries({ queryKey: connectionQueryKeys.detail(userId, connectionId) })
      queryClient.removeQueries({ queryKey: connectionQueryKeys.syncHistory(userId, connectionId) })
      queryClient.removeQueries({ queryKey: connectionQueryKeys.accounts(userId, connectionId) })
      void queryClient.invalidateQueries({
        queryKey: connectionQueryKeys.transactionFilters(userId),
      })
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.list(userId) }),
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.accounts(userId) }),
      ])
    },
  })
}
